'use client';

import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';
import type { EntityNotificationSettingsDialogProps } from './EntityNotificationSettingsDialog.types';
import type { EntityNotificationTypes } from '@/_types/entities';
import { Company } from '@/_types/models/company';
import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  Separator,
} from '@/_components';
import { NotificationList } from '@/_components/NotificationList';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { getCompany } from '@/_api/client-companies';
import {
  getNotificationTypes,
  saveNotificationPreference,
} from '@/_api/admin-notification-preferences';

/**
 * Notification settings dialog for editing single or bulk entities.
 *
 * If an array of only a single entity is passed, the dialog will pull up and
 * show the existing notification preferences for that entity.
 *
 * If multiple entities are passed, the dialog will overwrite any existing
 * notification preferences for them.
 *
 * In either case, notifications will not be disabled for entities of the users
 * the current admin is assigned to. In single entity mode, toggle switches in
 * the dialog will be disabled to reflect this. In bulk mode, the dialog has no
 * way of showing this, so the preference simply won't be saved for those
 * users. Questionnaire-document-related notifications are an exception to this
 * and are always allowed to be updated for assigned or non-assigned entities.
 */
export const EntityNotificationSettingsDialog: FC<
  EntityNotificationSettingsDialogProps
> = ({ isOpen, onClose, selectedEntityIds }) => {
  const { addToast } = useContext(ToastContext);
  const { user, token } = useContext(UserContext);
  const { preferences } = useContext(FirmSettingsContext);

  const [isLoading, setLoading] = useState<boolean>(false);

  const [singleEntity, setSingleEntity] = useState<Company | null>(null);

  const [selectedNotifications, setSelectedNotifications] = useState<
    Set<number>
  >(new Set());

  const isEditingSingleEntity = useMemo(
    () => selectedEntityIds?.length === 1,
    [selectedEntityIds],
  );

  const getSingleCompany = useCallback(async () => {
    setLoading(true);
    try {
      const singleEntityData = await getCompany({
        companyId: selectedEntityIds?.[0],
        token,
      });
      setSingleEntity(singleEntityData);
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
    }
  }, [selectedEntityIds, addToast, token]);

  const loadNotificationPreferences = useCallback(async () => {
    try {
      setLoading(true);
      const preferences = await getNotificationTypes({
        entityId: selectedEntityIds?.[0],
        token,
      });

      setSelectedNotifications(
        isEditingSingleEntity ? new Set(preferences) : new Set(),
      );
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
    }
  }, [selectedEntityIds, token, isEditingSingleEntity, addToast]);

  const hasLoadedNotificationPreferencesRef = useRef<boolean>(false);

  useEffect(() => {
    if (!token || !isOpen || hasLoadedNotificationPreferencesRef.current) {
      return;
    }
    hasLoadedNotificationPreferencesRef.current = true;
    loadNotificationPreferences();
    getSingleCompany();
  }, [token, isOpen, loadNotificationPreferences, getSingleCompany]);

  const onToggleNotification = (notificationId: number) => {
    setSelectedNotifications((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  const isSelfAssigned = useMemo(
    () =>
      isEditingSingleEntity &&
      Boolean(user?.id && singleEntity?.user?.assigned_advisor_id === user?.id),
    [isEditingSingleEntity, user, singleEntity],
  );

  const onSave = async () => {
    try {
      setLoading(true);

      const notificationTypeIds: EntityNotificationTypes = Array.from(
        selectedNotifications,
      );

      await saveNotificationPreference({
        token,
        entityIds: selectedEntityIds,
        notificationTypeIds,
      });

      addToast('success', 'Notification preference updated successfully');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const getIsDisabled = useCallback(
    (notificationId: number) => {
      const isChecked = selectedNotifications.has(notificationId);
      return (isSelfAssigned && isChecked) || isLoading;
    },
    [isSelfAssigned, isLoading, selectedNotifications],
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={`Notifications for ${singleEntity?.name}${selectedEntityIds?.length > 1 ? ` (+${selectedEntityIds?.length - 1} ${selectedEntityIds?.length === 2 ? 'other' : 'others'})` : ''}`}
    >
      <Dialog.Content>
        <Flex gap={6} isColumn={true}>
          {/* This logic handles three cases:
           * 1. Editing a single, self-assigned entity
           * 2. Editing a single, non-self-assigned entity
           * 3. Editing multiple entities
           */}
          {(isSelfAssigned || !isEditingSingleEntity) && (
            <>
              <Alert type="info">
                {isEditingSingleEntity
                  ? 'You cannot disable notifications for the entities of users you are assigned to.'
                  : 'Notifications cannot be disabled for entities of users you are assigned to. When bulk-editing ' +
                    'notification preferences, only preferences for entities of users you are not assigned to will be updated. ' +
                    'The notification preferences below will be applied to all selected entities.'}
              </Alert>
              <Separator />
            </>
          )}
          <NotificationList
            isGranular={!!preferences?.granular_notifications_enabled}
            questionnaireDocumentEnabled={
              !!preferences?.questionnaire_document_notification_enabled
            }
            selectedNotifications={selectedNotifications}
            onToggle={onToggleNotification}
            isDisabled={getIsDisabled}
          />
        </Flex>
      </Dialog.Content>
      <Dialog.Footer>
        <ButtonGroup align="right">
          <Button
            intent="secondary"
            value="Cancel"
            onClick={onClose}
            isDisabled={isLoading}
          />
          <Button
            intent="primary"
            value="Save"
            onClick={onSave}
            isDisabled={isLoading}
            isLoading={isLoading}
          />
        </ButtonGroup>
      </Dialog.Footer>
    </Dialog>
  );
};
